import { getPossibleOrientations, applySortStrategy, pruneContainedRects } from './common.js';
import { maximalRectanglesOptimize } from './maximalRectangles.js';

/**
 * Split free rectangles after placing a piece (MaxRects style)
 */
const splitFreeRects = (freeRects, placedPiece, cutWidth) => {
  const newFreeRects = [];
  const pieceRight = placedPiece.x + placedPiece.width + cutWidth;
  const pieceBottom = placedPiece.y + placedPiece.height + cutWidth;

  for (const rect of freeRects) {
    if (placedPiece.x >= rect.x + rect.width || pieceRight <= rect.x ||
        placedPiece.y >= rect.y + rect.height || pieceBottom <= rect.y) {
      newFreeRects.push(rect);
    } else {
      if (placedPiece.x > rect.x) {
        const leftWidth = placedPiece.x - rect.x - cutWidth;
        if (leftWidth > cutWidth) newFreeRects.push({ x: rect.x, y: rect.y, width: leftWidth, height: rect.height });
      }
      if (pieceRight < rect.x + rect.width) {
        const rightWidth = rect.x + rect.width - pieceRight;
        if (rightWidth > cutWidth) newFreeRects.push({ x: pieceRight, y: rect.y, width: rightWidth, height: rect.height });
      }
      if (placedPiece.y > rect.y) {
        const topHeight = placedPiece.y - rect.y - cutWidth;
        if (topHeight > cutWidth) newFreeRects.push({ x: rect.x, y: rect.y, width: rect.width, height: topHeight });
      }
      if (pieceBottom < rect.y + rect.height) {
        const bottomHeight = rect.y + rect.height - pieceBottom;
        if (bottomHeight > cutWidth) newFreeRects.push({ x: rect.x, y: pieceBottom, width: rect.width, height: bottomHeight });
      }
    }
  }
  return newFreeRects;
};

/**
 * MaxRects-BSSF-GLOBAL algorithm
 * 
 * Instead of placing pieces in sorted order, evaluates ALL unplaced pieces
 * against ALL free rectangles in ALL bins each round, placing the single
 * globally best fit. This is O(n²) more expensive but yields better packing.
 */
export const maxRectsGlobalOptimize = (allPieces, sheetWidth, sheetHeight, cutWidth, sortStrategy) => {
  const n = allPieces.length;
  const TIME_LIMIT_MS = Math.min(8000, Math.max(2000, n * 40));
  const startTime = performance.now();

  // Copy pieces with indices for tracking
  const pieces = allPieces.map((p, i) => ({ ...p, _idx: i }));
  const unplacedSet = new Set(pieces.map((_, i) => i));

  const bins = [];

  const createBin = () => ({
    id: bins.length + 1,
    width: sheetWidth,
    height: sheetHeight,
    pieces: [],
    freeRects: [{ x: 0, y: 0, width: sheetWidth, height: sheetHeight }],
  });

  let placementsSincePrune = 0;

  // GLOBAL placement loop
  while (unplacedSet.size > 0) {
    // Check time limit — use greedy fallback with existing bins
    if (performance.now() - startTime > TIME_LIMIT_MS) {
      const remainingPieces = pieces.filter((_, i) => unplacedSet.has(i));

      // Better fallback: try to fit remaining pieces into existing bins' freeRects first
      for (const piece of remainingPieces) {
        const orientations = getPossibleOrientations(piece);
        let placed = false;

        for (const orientation of orientations) {
          if (placed) break;
          for (const bin of bins) {
            let bestRect = null;
            let bestShort = Infinity;
            let bestLong = Infinity;

            for (const rect of bin.freeRects) {
              if (orientation.width <= rect.width && orientation.height <= rect.height) {
                const lH = rect.width - orientation.width;
                const lV = rect.height - orientation.height;
                const ss = Math.min(lH, lV);
                const ls = Math.max(lH, lV);
                if (ss < bestShort || (ss === bestShort && ls < bestLong)) {
                  bestShort = ss;
                  bestLong = ls;
                  bestRect = rect;
                }
              }
            }

            if (bestRect) {
              const placedPiece = { ...orientation, x: bestRect.x, y: bestRect.y };
              bin.pieces.push(placedPiece);
              unplacedSet.delete(pieces.indexOf(piece));
              bin.freeRects = splitFreeRects(bin.freeRects, placedPiece, cutWidth);
              if (bin.freeRects.length > 100) {
                bin.freeRects = pruneContainedRects(bin.freeRects);
              }
              placed = true;
              break;
            }
          }
        }

        // If doesn't fit in any existing bin, open a new one
        if (!placed) {
          const newBin = createBin();
          for (const orientation of orientations) {
            for (const rect of newBin.freeRects) {
              if (orientation.width <= rect.width && orientation.height <= rect.height) {
                const placedPiece = { ...orientation, x: rect.x, y: rect.y };
                newBin.pieces.push(placedPiece);
                unplacedSet.delete(pieces.indexOf(piece));
                newBin.freeRects = splitFreeRects(newBin.freeRects, placedPiece, cutWidth);
                placed = true;
                break;
              }
            }
            if (placed) break;
          }
          if (newBin.pieces.length > 0) bins.push(newBin);
        }
      }
      break;
    }

    // Ensure at least one bin exists
    if (bins.length === 0) {
      bins.push(createBin());
    }

    // Find the globally best placement
    let globalBest = null;

    searchLoop:
    for (const pieceIdx of unplacedSet) {
      const piece = pieces[pieceIdx];
      const orientations = getPossibleOrientations(piece);

      for (const orientation of orientations) {
        for (let binIdx = 0; binIdx < bins.length; binIdx++) {
          const bin = bins[binIdx];
          for (let rectIdx = 0; rectIdx < bin.freeRects.length; rectIdx++) {
            const rect = bin.freeRects[rectIdx];
            if (orientation.width <= rect.width && orientation.height <= rect.height) {
              const leftoverH = rect.width - orientation.width;
              const leftoverV = rect.height - orientation.height;
              const shortSide = Math.min(leftoverH, leftoverV);
              const longSide = Math.max(leftoverH, leftoverV);

              if (!globalBest ||
                  shortSide < globalBest.shortSide ||
                  (shortSide === globalBest.shortSide && longSide < globalBest.longSide)) {
                globalBest = {
                  pieceIdx,
                  orientation,
                  binIdx,
                  rect,
                  shortSide,
                  longSide,
                };
                // Early termination: perfect fit
                if (shortSide === 0) break searchLoop;
              }
            }
          }
        }
      }
    }

    if (globalBest) {
      // Place the best piece
      const bin = bins[globalBest.binIdx];
      const placedPiece = {
        ...globalBest.orientation,
        x: globalBest.rect.x,
        y: globalBest.rect.y,
      };
      bin.pieces.push(placedPiece);
      unplacedSet.delete(globalBest.pieceIdx);
      bin.freeRects = splitFreeRects(bin.freeRects, placedPiece, cutWidth);
      placementsSincePrune++;
      // Batch pruning: every 5 placements or when freeRects are large
      if (placementsSincePrune >= 5 || bin.freeRects.length > 100) {
        bin.freeRects = pruneContainedRects(bin.freeRects);
        placementsSincePrune = 0;
      }
    } else {
      // No piece fits in any existing bin — open a new one
      const newBin = createBin();
      bins.push(newBin);

      // Try again with the new bin — find best for new bin only
      let newBinBest = null;
      for (const pieceIdx of unplacedSet) {
        const piece = pieces[pieceIdx];
        const orientations = getPossibleOrientations(piece);
        for (const orientation of orientations) {
          for (const rect of newBin.freeRects) {
            if (orientation.width <= rect.width && orientation.height <= rect.height) {
              const leftoverH = rect.width - orientation.width;
              const leftoverV = rect.height - orientation.height;
              const shortSide = Math.min(leftoverH, leftoverV);
              const longSide = Math.max(leftoverH, leftoverV);
              if (!newBinBest || shortSide < newBinBest.shortSide ||
                  (shortSide === newBinBest.shortSide && longSide < newBinBest.longSide)) {
                newBinBest = { pieceIdx, orientation, rect, shortSide, longSide };
              }
            }
          }
        }
      }

      if (newBinBest) {
        const placedPiece = {
          ...newBinBest.orientation,
          x: newBinBest.rect.x,
          y: newBinBest.rect.y,
        };
        newBin.pieces.push(placedPiece);
        unplacedSet.delete(newBinBest.pieceIdx);
        newBin.freeRects = splitFreeRects(newBin.freeRects, placedPiece, cutWidth);
        newBin.freeRects = pruneContainedRects(newBin.freeRects);
        placementsSincePrune = 0;
      } else {
        // Truly unplaceable — remove all remaining as unplaced
        break;
      }
    }
  }

  const layouts = bins
    .filter(b => b.pieces.length > 0)
    .map((bin, idx) => ({
      id: idx + 1,
      width: bin.width,
      height: bin.height,
      pieces: bin.pieces,
    }));

  const unplacedPieces = pieces.filter((_, i) => unplacedSet.has(i));

  return { layouts, unplacedPieces, unplacedCount: unplacedPieces.length };
};
